import { cn } from "@/utils/utils";

export type TSegmentedToggleOption<TValue extends string> = {
    value: TValue;
    label: string;
};

export interface TSegmentedToggleProps<TValue extends string> {
    /** Accessible name for the group — e.g. "Type", "Board view". */
    label: string;
    options: TSegmentedToggleOption<TValue>[];
    value: TValue;
    onChange: (value: TValue) => void;
    className?: string;
}

/**
 * Small inline segmented control — the `[ Service providers | PortCo's ]` and
 * `[ Accountant | Service provider ]` toggles on the Homepage board (design
 * §7.1, §7.3).
 *
 * Always has exactly one selected segment, so "no filter" has to be one of the
 * options ("All") rather than a deselected state: a toggle you can click into
 * an empty state is hard to discover your way back out of.
 */
export function SegmentedToggle<TValue extends string>({
    label,
    options,
    value,
    onChange,
    className,
}: TSegmentedToggleProps<TValue>) {
    return (
        <div
            role="group"
            aria-label={label}
            className={cn("inline-flex rounded-xl border bg-muted/40 p-1", className)}
        >
            {options.map((option) => {
                const selected = option.value === value;
                return (
                    <button
                        key={option.value}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => onChange(option.value)}
                        className={cn(
                            "rounded-lg px-4 py-2 text-sm font-medium transition-colors",
                            selected
                                ? "bg-card text-foreground shadow-proda-sm"
                                : "text-muted-foreground hover:text-foreground",
                        )}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}
